import {Badge, Box, Grid, Multiselect, Pagination, Table, TableProps} from '@amzn/awsui-components-react';
import React, {useMemo, useState} from 'react';
import {useCollection} from '@amzn/awsui-collection-hooks';
import {InboundBridgeChannelPlanActual, NodeFilterData} from './index';
import * as awsui from '@amzn/awsui-design-tokens/polaris';

const TABLE_PAGE_SIZE = 30;
const fmt = (val: number) => val.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0});

export type ByNodeRecord = {
    id: string;
    parentId: string | null;
    level: 'week' | 'node' | 'day';
    // label shown in the Date column; node name shown in Warehouse column
    label: string;
    node: string;
    planTotal: number;
    actualTotal: number;
    deltaTotal: number;
    byChannel: Record<string, {plan: number; actual: number; delta: number}>;
};

function buildColumnDefinitions(
    channels: string[],
    bridgeType: 'p2a' | 'p2p' = 'p2a'
): TableProps.ColumnDefinition<ByNodeRecord>[] {
    const deltaCell = (val: number) => {
        const color = val < 0 ? awsui.colorChartsRed600 : val > 0 ? awsui.colorChartsTeal600 : 'inherit';
        return <span style={{color, fontWeight: 500}}>{fmt(val)}</span>;
    };

    const dateCol: TableProps.ColumnDefinition<ByNodeRecord> = {
        id: 'date',
        header: 'Date / Warehouse',
        cell: (item) => {
            if (item.level !== 'day') return item.label;
            const d = new Date(item.label + 'T00:00:00');
            const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
            return `${item.label} (${dow})`;
        },
        isRowHeader: true,
    };

    const planLabel = bridgeType === 'p2p' ? 'From Plan' : 'Plan';
    const actualLabel = bridgeType === 'p2p' ? 'To Plan' : 'Actual';

    const planTotalCol: TableProps.ColumnDefinition<ByNodeRecord> = {
        id: 'plan_total',
        header: (
            <span>
                {planLabel} <Badge color="blue">Total</Badge>
            </span>
        ),
        cell: (item) => <b>{fmt(item.planTotal)}</b>,
    };

    const actualTotalCol: TableProps.ColumnDefinition<ByNodeRecord> = {
        id: 'actual_total',
        header: (
            <span>
                {actualLabel} <Badge color="blue">Total</Badge>
            </span>
        ),
        cell: (item) => <b>{fmt(item.actualTotal)}</b>,
    };

    const deltaTotalCol: TableProps.ColumnDefinition<ByNodeRecord> = {
        id: 'delta_total',
        header: (
            <span>
                Delta <Badge color="blue">Total</Badge>
            </span>
        ),
        cell: (item) => {
            const val = item.deltaTotal;
            const color = val < 0 ? awsui.colorChartsRed600 : val > 0 ? awsui.colorChartsTeal600 : 'inherit';
            return <b style={{color}}>{fmt(val)}</b>;
        },
    };

    const channelPlanCols = channels.map(
        (ch): TableProps.ColumnDefinition<ByNodeRecord> => ({
            id: `plan_${ch}`,
            header: ch,
            cell: (item) => fmt(item.byChannel[ch]?.plan ?? 0),
        })
    );
    const channelActualCols = channels.map(
        (ch): TableProps.ColumnDefinition<ByNodeRecord> => ({
            id: `actual_${ch}`,
            header: ch,
            cell: (item) => fmt(item.byChannel[ch]?.actual ?? 0),
        })
    );
    const channelDeltaCols = channels.map(
        (ch): TableProps.ColumnDefinition<ByNodeRecord> => ({
            id: `delta_${ch}`,
            header: ch,
            cell: (item) => deltaCell(item.byChannel[ch]?.delta ?? 0),
        })
    );

    return [dateCol, planTotalCol, ...channelPlanCols, actualTotalCol, ...channelActualCols, deltaTotalCol, ...channelDeltaCols];
}

export function InboundBridgeChannelPlanActualByNodeTable({
    bridgeData,
    isLoading,
    bridgeType = 'p2a',
}: {
    bridgeData: InboundBridgeChannelPlanActual[];
    selectedFilter: NodeFilterData | undefined;
    isLoading: boolean;
    bridgeType?: 'p2a' | 'p2p';
}) {
    const [tableSelectedWeeks, setTableSelectedWeeks] = useState<string[]>([]);
    const [tableSelectedNodes, setTableSelectedNodes] = useState<string[]>([]);
    const [expandedItems, setExpandedItems] = useState<ByNodeRecord[]>([]);

    // Reset local filters when the dataset is refreshed
    const prevBridgeDataRef = React.useRef(bridgeData);
    if (prevBridgeDataRef.current !== bridgeData) {
        prevBridgeDataRef.current = bridgeData;
        if (tableSelectedWeeks.length) setTableSelectedWeeks([]);
        if (tableSelectedNodes.length) setTableSelectedNodes([]);
    }

    const tableEmpty = (
        <Box textAlign="center" color="inherit">
            <b>No matching data</b>
        </Box>
    );

    const {rows, channels} = useMemo(() => processByNode(bridgeData), [bridgeData]);
    // top-level items are week rows
    const weekRows = useMemo(() => rows.filter((r) => r.level === 'week'), [rows]);
    const columnDefs = useMemo(() => buildColumnDefinitions(channels, bridgeType), [channels, bridgeType]);

    const distinctNodes = useMemo(
        () => Array.from(new Set(rows.filter((r) => r.level === 'node').map((r) => r.node))).sort(),
        [rows]
    );

    const {items, paginationProps} = useCollection(weekRows, {
        filtering: {
            empty: tableEmpty,
            noMatch: tableEmpty,
            filteringFunction(item) {
                if (tableSelectedWeeks.length > 0 && !tableSelectedWeeks.includes(item.label)) return false;
                if (tableSelectedNodes.length > 0) {
                    const nodeChildren = rows.filter((r) => r.parentId === item.id);
                    return nodeChildren.some((c) => tableSelectedNodes.includes(c.node));
                }
                return true;
            },
        },
        pagination: {pageSize: TABLE_PAGE_SIZE},
    });

    return (
        <Table
            data-testid="channel-plan-actual-by-node-table"
            variant="embedded"
            items={items}
            expandableRows={{
                getItemChildren: (item) => {
                    const children = rows.filter((r) => r.parentId === item.id);
                    // filter node-level children by warehouse selection
                    if (item.level === 'week' && tableSelectedNodes.length > 0) {
                        return children.filter((c) => tableSelectedNodes.includes(c.node));
                    }
                    return children;
                },
                isItemExpandable: (item) => item.level !== 'day',
                expandedItems,
                onExpandableItemToggle: ({detail}) => {
                    setExpandedItems((prev) =>
                        detail.expanded ? [...prev, detail.item] : prev.filter((i) => i.id !== detail.item.id)
                    );
                },
            }}
            stickyHeader
            stripedRows
            empty={tableEmpty}
            pagination={<Pagination {...paginationProps} />}
            filter={
                <Grid gridDefinition={[{colspan: 3}, {colspan: 3}]}>
                    <Multiselect
                        data-testid="by-node-table-week-multiselect"
                        selectedOptions={tableSelectedWeeks.map((w) => ({label: w, value: w}))}
                        onChange={({detail}) => setTableSelectedWeeks(detail.selectedOptions.map((o) => o.value!))}
                        options={weekRows.map((r) => ({label: r.label, value: r.label}))}
                        tokenLimit={5}
                        filteringType="auto"
                        placeholder="Filter by week"
                    />
                    <Multiselect
                        data-testid="by-node-table-warehouse-multiselect"
                        selectedOptions={tableSelectedNodes.map((n) => ({label: n, value: n}))}
                        onChange={({detail}) => setTableSelectedNodes(detail.selectedOptions.map((o) => o.value!))}
                        options={distinctNodes.map((n) => ({label: n, value: n}))}
                        tokenLimit={5}
                        filteringType="auto"
                        placeholder="Filter by warehouse"
                    />
                </Grid>
            }
            columnDefinitions={columnDefs}
            loading={isLoading}
            enableKeyboardNavigation
            renderAriaLive={({firstIndex, lastIndex, totalItemsCount, visibleItemsCount}) =>
                `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}, ${visibleItemsCount} visible`
            }
        />
    );
}

// Tree: week -> node -> day
function processByNode(records: InboundBridgeChannelPlanActual[]): {rows: ByNodeRecord[]; channels: string[]} {
    const channelSet = new Set<string>();
    records.forEach((r) => channelSet.add(r.channel));
    const channels = Array.from(channelSet).sort();

    // node -> date -> channel -> {plan, actual}
    const nodeMap = new Map<string, Map<string, Map<string, {plan: number; actual: number}>>>();
    records.forEach((record) => {
        if (!nodeMap.has(record.node)) nodeMap.set(record.node, new Map());
        const dateMap = nodeMap.get(record.node)!;
        if (!dateMap.has(record.date)) dateMap.set(record.date, new Map());
        const chMap = dateMap.get(record.date)!;
        if (!chMap.has(record.channel)) chMap.set(record.channel, {plan: 0, actual: 0});
        const entry = chMap.get(record.channel)!;
        if (record.name === 'Plan' || record.name === 'FromPlan') entry.plan += record.value;
        else if (record.name === 'Actual' || record.name === 'ToPlan') entry.actual += record.value;
    });

    // collect all dates to build week groupings
    const allDates = new Set<string>();
    nodeMap.forEach((dateMap) => dateMap.forEach((_, date) => allDates.add(date)));
    const weekMap = new Map<string, Set<string>>();
    allDates.forEach((date) => {
        const sunday = getSundayOfWeek(date);
        if (!weekMap.has(sunday)) weekMap.set(sunday, new Set());
        weekMap.get(sunday)!.add(date);
    });

    const rows: ByNodeRecord[] = [];
    let id = 0;
    const sortedSundays = Array.from(weekMap.keys()).sort();
    const sortedNodes = Array.from(nodeMap.keys()).sort();

    sortedSundays.forEach((sunday) => {
        const datesInWeek = Array.from(weekMap.get(sunday)!).sort();
        const weekId = `week-${id++}`;
        const weekByChannel: Record<string, {plan: number; actual: number; delta: number}> = {};
        channels.forEach((ch) => (weekByChannel[ch] = {plan: 0, actual: 0, delta: 0}));
        let weekPlan = 0,
            weekActual = 0;

        // collect node/day rows first to compute week totals, then push in order
        const nodeAndDayRows: ByNodeRecord[] = [];

        sortedNodes.forEach((node) => {
            const dateMap = nodeMap.get(node)!;
            const nodeDates = datesInWeek.filter((d) => dateMap.has(d));
            if (nodeDates.length === 0) return;

            const nodeId = `node-${id++}`;
            const nodeByChannel: Record<string, {plan: number; actual: number; delta: number}> = {};
            channels.forEach((ch) => (nodeByChannel[ch] = {plan: 0, actual: 0, delta: 0}));
            let nodePlan = 0,
                nodeActual = 0;

            nodeDates.forEach((date) => {
                const chMap = dateMap.get(date)!;
                const byChannel: Record<string, {plan: number; actual: number; delta: number}> = {};
                let dayPlan = 0,
                    dayActual = 0;

                channels.forEach((ch) => {
                    const {plan, actual} = chMap.get(ch) ?? {plan: 0, actual: 0};
                    const delta = actual - plan;
                    byChannel[ch] = {plan, actual, delta};
                    dayPlan += plan;
                    dayActual += actual;
                    nodeByChannel[ch].plan += plan;
                    nodeByChannel[ch].actual += actual;
                    nodeByChannel[ch].delta += delta;
                });

                nodePlan += dayPlan;
                nodeActual += dayActual;

                nodeAndDayRows.push({
                    id: `day-${id++}`,
                    parentId: nodeId,
                    level: 'day',
                    label: date,
                    node,
                    planTotal: dayPlan,
                    actualTotal: dayActual,
                    deltaTotal: dayActual - dayPlan,
                    byChannel,
                });
            });

            channels.forEach((ch) => {
                weekByChannel[ch].plan += nodeByChannel[ch].plan;
                weekByChannel[ch].actual += nodeByChannel[ch].actual;
                weekByChannel[ch].delta += nodeByChannel[ch].delta;
            });
            weekPlan += nodePlan;
            weekActual += nodeActual;

            nodeAndDayRows.push({
                id: nodeId,
                parentId: weekId,
                level: 'node',
                label: node,
                node,
                planTotal: nodePlan,
                actualTotal: nodeActual,
                deltaTotal: nodeActual - nodePlan,
                byChannel: nodeByChannel,
            });
        });

        // push week row first (ascending order), then its children
        rows.push({
            id: weekId,
            parentId: null,
            level: 'week',
            label: `Week of ${sunday}`,
            node: '',
            planTotal: weekPlan,
            actualTotal: weekActual,
            deltaTotal: weekActual - weekPlan,
            byChannel: weekByChannel,
        });
        rows.push(...nodeAndDayRows);
    });

    return {rows, channels};
}

function getSundayOfWeek(dateStr: string): string {
    const date = new Date(dateStr + 'T00:00:00');
    date.setUTCDate(date.getUTCDate() - date.getUTCDay());
    return date.toISOString().split('T')[0];
}
